import { c as _c } from "react/compiler-runtime";
import { Editor } from '@monaco-editor/react';
import { IcoMaximize16, IcoMinimize16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { AnimatePresence, motion, useSpring } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Shimmer } from '../..';
import { useMonaco } from '../monaco/monaco-provider';
import { type PythonAutocompleteItem, pythonModelAutocomplete } from '../monaco/python-autocomplete';
import { getVariantSpecificMonacoConfig } from '../monaco/utils';
type AutocompleteItems = {
  tokens?: {
    display: string;
    token: string;
  }[];
};
export type PythonCodeEditorProps = {
  value: string;
  onChange: (value: string) => void;
  autocompleteItems?: AutocompleteItems;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
  height?: number;
  isExpandable?: boolean;
  expandedHeight?: number;
};
export const PythonCodeEditor = t0 => {
  const $ = _c(51);
  const {
    value,
    onChange,
    autocompleteItems,
    disabled: t1,
    placeholder,
    className,
    hasError,
    height: t2,
    isExpandable: t3,
    expandedHeight
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const height = t2 === undefined ? 328 : t2;
  const isExpandable = t3 === undefined ? true : t3;
  const {
    monaco
  } = useMonaco();
  const editorRef = useRef(null);
  const mountedModelRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      stiffness: 300,
      damping: 30
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const animatedHeight = useSpring(height, t4);
  const [currentHeight, setCurrentHeight] = useState(height);
  autocompleteItems?.tokens;
  let t5;
  if ($[1] !== autocompleteItems?.tokens) {
    t5 = autocompleteItems?.tokens ?? [];
    $[1] = autocompleteItems?.tokens;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t5) {
    t6 = t5.map(_temp);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const tokens = t6;
  let t7;
  if ($[5] !== monaco || $[6] !== tokens) {
    t7 = editor => {
      if (!monaco) {
        return;
      }
      editorRef.current = editor;
      const model = editor.getModel();
      if (model) {
        mountedModelRef.current = model;
        pythonModelAutocomplete.set(model, tokens);
      }
      editor.onDidChangeModel(e => {
        if (e.oldModelUrl && mountedModelRef.current) {
          pythonModelAutocomplete.delete(mountedModelRef.current);
        }
        const newModel = editor.getModel();
        mountedModelRef.current = newModel ?? null;
        if (newModel) {
          pythonModelAutocomplete.set(newModel, tokens);
        }
      });
    };
    $[5] = monaco;
    $[6] = tokens;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const onMount = t7;
  let t8;
  let t9;
  if ($[8] !== tokens) {
    t8 = () => {
      const model_0 = mountedModelRef.current;
      if (model_0) {
        pythonModelAutocomplete.set(model_0, tokens);
      }
    };
    t9 = [tokens];
    $[8] = tokens;
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[11] !== animatedHeight || $[12] !== expandedHeight || $[13] !== height || $[14] !== isExpanded) {
    t10 = () => {
      const targetHeight = isExpanded ? expandedHeight ?? height * 2 : height;
      animatedHeight.set(targetHeight);
    };
    t11 = [isExpanded, height, expandedHeight, animatedHeight];
    $[11] = animatedHeight;
    $[12] = expandedHeight;
    $[13] = height;
    $[14] = isExpanded;
    $[15] = t10;
    $[16] = t11;
  } else {
    t10 = $[15];
    t11 = $[16];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[17] !== animatedHeight) {
    t12 = () => {
      const unsubscribe = animatedHeight.on("change", latest => {
        setCurrentHeight(Math.round(latest));
      });
      return unsubscribe;
    };
    t13 = [animatedHeight];
    $[17] = animatedHeight;
    $[18] = t12;
    $[19] = t13;
  } else {
    t12 = $[18];
    t13 = $[19];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => () => {
      if (editorRef.current) {
        editorRef.current.dispose();
      }
    };
    t15 = [];
    $[20] = t14;
    $[21] = t15;
  } else {
    t14 = $[20];
    t15 = $[21];
  }
  useEffect(t14, t15);
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      onFocus: () => setIsFocused(true),
      onBlur: () => setIsFocused(false)
    };
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const focusAndHoverEvents = t16;
  const shouldShowExpandButton = (isHovered || isFocused) && isExpandable;
  if (!monaco) {
    const t17 = `w-full h-[${height}px]`;
    let t18;
    if ($[23] !== t17) {
      t18 = <Shimmer className={t17} />;
      $[23] = t17;
      $[24] = t18;
    } else {
      t18 = $[24];
    }
    return t18;
  }
  let t17;
  if ($[25] !== className || $[26] !== disabled || $[27] !== hasError) {
    t17 = cx("\n            relative w-full rounded-md\n            outline-none caret-black\n            pt-[5px] px-3\n            [&_.monaco-editor]:absolute!\n            [&_.margin-view-overlays]:bg-secondary!\n            [&_.monaco-editor]:absolute!\n            [&_.monaco-editor-background]:bg-secondary!\n            [&_.margin-view-overlays]:bg-secondary!\n            [&_.sticky-line-content]:bg-secondary!\n            [&_.sticky-line-number]:bg-secondary!\n            [&_.squiggly-error]:hidden!\n            [&_.squiggly-d-error]:hidden!\n            [&_.zone-widget]:hidden!\n            [&_.findMatch]:bg-transparent!\n            [&_.ime-input]:bg-transparent!\n          ", {
      "bg-secondary [&_.monaco-editor-background]:bg-secondary!": true,
      "border-error!": hasError,
      "shadow-focus-error hover:shadow-none": hasError,
      "bg-secondary! [&_.monaco-editor-background]:bg-secondary! border-tertiary! opacity-70 grayscale-60": disabled
    }, className);
    $[25] = className;
    $[26] = disabled;
    $[27] = hasError;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = `${currentHeight}px`;
  let t19;
  if ($[29] !== onChange) {
    t19 = value_0 => onChange(value_0 ?? "");
    $[29] = onChange;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Shimmer className="w-full h-full" />;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = isExpandable ? 56 : 12;
  let t22;
  if ($[32] !== disabled || $[33] !== placeholder || $[34] !== t21) {
    t22 = getVariantSpecificMonacoConfig({
      variant: "textarea",
      placeholder,
      size: "large",
      lineNumbers: "on",
      disabled,
      fontFamily: "\"DM Mono\", \"SFMono-Regular\", Consolas, \"Liberation Mono\", Menlo, Courier, monospace",
      fontWeight: "400",
      fontSize: 13,
      lineDecorationsWidth: 12,
      lineHeight: 20,
      padding: {
        top: 12,
        bottom: t21
      },
      quickSuggestions: {
        other: true,
        comments: true,
        strings: true
      }
    });
    $[32] = disabled;
    $[33] = placeholder;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== onMount || $[37] !== t17 || $[38] !== t18 || $[39] !== t19 || $[40] !== t22 || $[41] !== value) {
    t23 = <Editor wrapperProps={focusAndHoverEvents} className={t17} height={t18} language="python" value={value} onMount={onMount} onChange={t19} loading={t20} options={t22} />;
    $[36] = onMount;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
    $[40] = t22;
    $[41] = value;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== isExpanded || $[44] !== shouldShowExpandButton) {
    t24 = shouldShowExpandButton && <motion.div className="absolute! bottom-0 left-0 z-100 bg-secondary! w-full p-2 pointer-events-auto!" {...focusAndHoverEvents} initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 10
    }} transition={{
      duration: 0.2
    }}><Button prefixIcon={isExpanded ? IcoMinimize16 : IcoMaximize16} variant="secondary" size="compact" type="button" onClick={() => setIsExpanded(!isExpanded)} className="bg-transparent! border-0! shadow-none!">{isExpanded ? "Collapse" : "Expand"}</Button></motion.div>;
    $[43] = isExpanded;
    $[44] = shouldShowExpandButton;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t24) {
    t25 = <AnimatePresence>{t24}</AnimatePresence>;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t23 || $[49] !== t25) {
    t26 = <div className="overflow-visible w-full relative" {...focusAndHoverEvents}>{t23}{t25}</div>;
    $[48] = t23;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  return t26;
};
function _temp(t) {
  return {
    display: t.display,
    token: t.token
  };
}